import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  BookOpen,
  Package,
  Calendar,
  ShoppingCart,
  FileText,
  Truck,
  CreditCard,
  Shield,
  Users,
  UserPlus,
  UserCheck,
  FolderTree,
  List,
  Plus,
  Eye,
  Check,
  Filter,
  Search,
  ArrowLeft,
  Hash,
  Building2,
  TruckIcon
} from "lucide-react";
import "./dashboard.css";
import BackButton from "../../components/backbutton";

export default function SalesDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("create-ledger");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCustomerProducts, setShowCustomerProducts] = useState(false);
  const [showSupplierProducts, setShowSupplierProducts] = useState(false);
  const [showGroupCustomers, setShowGroupCustomers] = useState(false);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    paymentMode: "",
    paymentDate: "",
    utrNumber: "",
    verified: false
  });

  // Sample Customer Data
  const [customers] = useState([
    { 
      id: "CUST-001", 
      pocName: "John Doe", 
      companyName: "ABC Corp", 
      username: "abccorp", 
      password: "****", 
      address: "123 Main St, Chennai", 
      pan: "ABCDE1234F", 
      bankDetails: "ICICI Bank, Acc: 123456789", 
      gst: "22AAAAA0000A1Z",
      vendorCode: "VC001",
      products: ["Product A", "Product B", "Product C"]
    },
    { 
      id: "CUST-002", 
      pocName: "Jane Smith", 
      companyName: "XYZ Ltd", 
      username: "xyzltd", 
      password: "****", 
      address: "456 Oak Rd, Mumbai", 
      pan: "FGHIJ5678K", 
      bankDetails: "HDFC Bank, Acc: 987654321", 
      gst: "27BBBBB0000B2Z",
      vendorCode: "VC002",
      products: ["Product D", "Product E"]
    }
  ]);

  // Sample Supplier Data
  const [suppliers] = useState([
    { 
      id: "SUP-001", 
      pocName: "Mike Wilson", 
      companyName: "ABC Suppliers", 
      username: "abcsuppliers", 
      password: "****", 
      address: "789 Industrial Area, Pune", 
      pan: "LMNOP9012Q", 
      bankDetails: "SBI Bank, Acc: 555555555", 
      gst: "27CCCCC0000C3Z",
      vendorCode: "VS001",
      products: ["Raw Material X", "Raw Material Y"]
    },
    { 
      id: "SUP-002", 
      pocName: "Sarah Johnson", 
      companyName: "XYZ Enterprises", 
      username: "xyzent", 
      password: "****", 
      address: "321 Business Park, Delhi", 
      pan: "QRSTU3456V", 
      bankDetails: "Axis Bank, Acc: 444444444", 
      gst: "07DDDDD0000D4Z",
      vendorCode: "VS002",
      products: ["Machine Parts", "Tools"]
    }
  ]);

  // Sample Groups
  const [groups] = useState([
    { id: "GRP-001", name: "Premium Customers", members: ["CUST-001", "CUST-002"] },
    { id: "GRP-002", name: "Regular Customers", members: [] }
  ]);

  // Sample Schedule Data
  const [schedules] = useState([
    { 
      id: "SCH-001", 
      category: "Electronics", 
      subCategory: "Mobile Phones", 
      date: "2024-04-10", 
      group: "Premium Customers",
      productName: "Product A",
      quantity: 100,
      weekly: true,
      monthly: false,
      month: null,
      validTill: "2024-12-31",
      finishedGoods: 150,
      productionPending: 50,
      outsourcePending: 20,
      rawMaterial: 500
    }
  ]);

  // Sample Sale Orders
  const [saleOrders, setSaleOrders] = useState([
    { id: "SO-001", orderNumber: "SO-2024-001", customer: "ABC Corp", product: "Product A", quantity: 100, amount: "₹1,50,000", date: "2024-03-15", status: "Pending", priority: "High" },
    { id: "SO-002", orderNumber: "SO-2024-002", customer: "XYZ Ltd", product: "Product B", quantity: 50, amount: "₹85,000", date: "2024-03-14", status: "Pending", priority: "Medium" }
  ]);

  // Sample Invoices
  const [invoices] = useState([
    { id: "INV-001", invoiceNumber: "INV-2024-001", customer: "ABC Corp", schedule: "SCH-001", date: "2024-03-10", productGroup: "Electronics", amount: "₹1,50,000", cgst: "₹13,500", sgst: "₹13,500", igst: "₹0", status: "Pending" },
    { id: "INV-002", invoiceNumber: "INV-2024-002", customer: "XYZ Ltd", schedule: "SCH-002", date: "2024-03-12", productGroup: "Machinery", amount: "₹85,000", cgst: "₹7,650", sgst: "₹7,650", igst: "₹0", status: "Pending" }
  ]);

  // Sample Products Data
  const [categories,] = useState([
    { id: "CAT-001", name: "Electronics", products: ["Mobile Phones", "Laptops", "Tablets"] },
    { id: "CAT-002", name: "Machinery", products: ["Machine Parts", "Tools", "Equipment"] }
  ]);

  // const [setProducts] = useState([
  //   { id: "PROD-001", name: "Product A", category: "Electronics", price: "₹1,500", group: "Premium" },
  //   { id: "PROD-002", name: "Product B", category: "Machinery", price: "₹1,700", group: "Regular" }
  // ]);

  // const [productGroups, setProductGroups] = useState([
  //   { id: "PG-001", name: "Premium Products", products: ["Product A"] },
  //   { id: "PG-002", name: "Regular Products", products: ["Product B"] }
  // ]);

  // Sidebar menu items
  const tabItems = [
    { id: "create-ledger", label: "Create Ledger", icon: <BookOpen size={18} /> },
    { id: "debtor", label: "Debtor", icon: <Users size={18} /> },
    { id: "create-customer", label: "Create Customer", icon: <UserPlus size={18} /> },
    { id: "list-customer", label: "List Customer", icon: <UserCheck size={18} /> },
    { id: "creditor", label: "Creditor", icon: <Building2 size={18} /> },
    { id: "create-supplier", label: "Create Supplier", icon: <UserPlus size={18} /> },
    { id: "list-supplier", label: "List Supplier", icon: <UserCheck size={18} /> },
    { id: "product", label: "Product", icon: <Package size={18} /> },
    { id: "create-schedule", label: "Create Schedule", icon: <Calendar size={18} /> },
    { id: "list-schedule", label: "List Schedule", icon: <List size={18} /> },
    { id: "list-sale-order", label: "List Sale Order", icon: <ShoppingCart size={18} /> },
    { id: "list-invoice", label: "List Invoice", icon: <FileText size={18} /> },
    { id: "dispatch", label: "Dispatch", icon: <Truck size={18} /> },
    { id: "customer-payment", label: "Debtor / Customer Invoice Payment", icon: <CreditCard size={18} /> },
    { id: "security", label: "Security", icon: <Shield size={18} /> }
  ];

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  const getPageTitle = () => {
    const tab = tabItems.find(item => item.id === activeTab);
    return tab ? tab.label : "Sales Dashboard";
  };

  // Handle security page navigation
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "security") {
      navigate('/security-dashboard');
    }
  };

  // Handle Create Customer Submit
  const handleCreateCustomer = (e) => {
    e.preventDefault();
    alert("Customer created successfully! Vendor Code: VC" + Math.floor(Math.random() * 10000));
  };

  // Handle Create Supplier Submit
  const handleCreateSupplier = (e) => {
    e.preventDefault();
    alert("Supplier created successfully! Vendor Code: VS" + Math.floor(Math.random() * 10000));
  };

  // Handle Sale Order Approval
  const handleApproveSaleOrder = (orderId) => {
    setSaleOrders(saleOrders.map(o => o.id === orderId ? { ...o, status: "Approved" } : o));
    alert(`Sale Order ${orderId} has been approved!`);
  };

  // Handle Customer Payment
  const handleCustomerPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleSubmitPayment = () => {
    alert(`Payment received from customer!\nMode: ${paymentDetails.paymentMode}\nDate: ${paymentDetails.paymentDate}\nUTR: ${paymentDetails.utrNumber}\nVerified: ${paymentDetails.verified ? "Yes" : "No"}`);
    setShowPaymentModal(false);
    setPaymentDetails({ paymentMode: "", paymentDate: "", utrNumber: "", verified: false });
  };

  // Filter function
  const filteredData = (data, searchFields) => {
    if (!searchTerm) return data;
    return data.filter(item => 
      searchFields.some(field => 
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  // Create Ledger Component
  const CreateLedgerContent = () => (
    <div className="content-container">
      <div className="ledger-options">
        <div className="ledger-card" onClick={() => setActiveTab("create-customer")}>
          <div className="ledger-icon"><UserPlus size={32} color="#4F46E5" /></div>
          <h3>Create Customer</h3>
          <p>Add new customer to ledger</p>
        </div>
        <div className="ledger-card" onClick={() => setActiveTab("create-supplier")}>
          <div className="ledger-icon"><Building2 size={32} color="#10B981" /></div>
          <h3>Create Supplier</h3>
          <p>Add new supplier to ledger</p>
        </div>
      </div>
    </div>
  );

  // Debtor Component (Customer List)
  const DebtorContent = () => (
    <div className="content-container">
      <div className="filter-bar">
        <Search size={18} />
        <input type="text" placeholder="Search customers..." className="search-input" 
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer Code</th>
              <th>Company Name</th>
              <th>POC Name</th>
              <th>GST</th>
              <th>Outstanding</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData(customers, ['companyName', 'pocName', 'vendorCode']).map(cust => (
              <tr key={cust.id}>
                <td className="highlight">{cust.vendorCode}</td>
                <td>{cust.companyName}</td>
                <td>{cust.pocName}</td>
                <td>{cust.gst}</td>
                <td className="amount">₹0</td>
                <td className="action-buttons">
                  <button className="action-btn view" onClick={() => alert(`Customer Details:\nCompany: ${cust.companyName}\nPOC: ${cust.pocName}\nAddress: ${cust.address}`)}><Eye size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Create Customer Component
  const CreateCustomerContent = () => (
    <div className="content-container">
      <form className="customer-form" onSubmit={handleCreateCustomer}>
        <div className="form-grid">
          <div className="form-group"><label>POC Name *</label><input type="text" className="form-input" required placeholder="Enter POC name" /></div>
          <div className="form-group"><label>Company Name *</label><input type="text" className="form-input" required placeholder="Enter company name" /></div>
          <div className="form-group"><label>Username *</label><input type="text" className="form-input" required placeholder="Enter username" /></div>
          <div className="form-group"><label>Password *</label><input type="password" className="form-input" required placeholder="Enter password" /></div>
          <div className="form-group full-width"><label>Address *</label><textarea className="form-textarea" rows="2" placeholder="Enter address"></textarea></div>
          <div className="form-group"><label>PAN Number</label><input type="text" className="form-input" placeholder="Enter PAN" /></div>
          <div className="form-group"><label>Bank Details</label><input type="text" className="form-input" placeholder="Enter bank details" /></div>
          <div className="form-group"><label>GST Number</label><input type="text" className="form-input" placeholder="Enter GST" /></div>
          <div className="form-group"><label>Add Excess Fields</label><input type="text" className="form-input" placeholder="Additional fields" /></div>
          <div className="form-group"><label>Vendor Code</label><input type="text" className="form-input" value="Auto Generated" disabled /></div>
        </div>
        <div className="form-actions"><button type="submit" className="btn-submit"><Check size={16} /> Submit</button></div>
      </form>
    </div>
  );

  // List Customer Component
  const ListCustomerContent = () => (
    <div className="content-container">
      <div className="filter-bar">
        <Search size={18} />
        <input type="text" placeholder="Search customers..." className="search-input" 
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>
      <div className="customer-list">
        {filteredData(customers, ['companyName', 'vendorCode']).map(cust => (
          <div key={cust.id} className="customer-card">
            <div className="customer-header"><h3>{cust.companyName}</h3><span className="vendor-code">{cust.vendorCode}</span></div>
            <div className="customer-details"><p><strong>POC:</strong> {cust.pocName}</p><p><strong>GST:</strong> {cust.gst}</p></div>
            <div className="customer-actions">
              <button className="action-btn" onClick={() => { setSelectedCustomer(cust); setShowCustomerProducts(true); }}>Select Product</button>
              <button className="action-btn view" onClick={() => alert(`Customer Details:\nCompany: ${cust.companyName}\nAddress: ${cust.address}\nBank: ${cust.bankDetails}`)}><Eye size={16} /> View</button>
            </div>
          </div>
        ))}
      </div>

      {/* Group Section */}
      <div className="group-section"><h3>Group Customer</h3>
        <div className="group-list">{groups.map(group => (
          <div key={group.id} className="group-card" onClick={() => { setSelectedGroup(group); setShowGroupCustomers(true); }}>
            <h4>{group.name}</h4><p>{group.members.length} members</p>
          </div>
        ))}</div>
        <button className="btn-add" onClick={() => alert("Create New Group")}><Plus size={16} /> Create New Group</button>
      </div>

      {/* Customer Products Modal */}
      {showCustomerProducts && selectedCustomer && (
        <div className="modal-overlay" onClick={() => setShowCustomerProducts(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><button className="back-btn" onClick={() => setShowCustomerProducts(false)}><ArrowLeft size={20} /> Back</button><h2>Select Products for {selectedCustomer.companyName}</h2><button className="modal-close" onClick={() => setShowCustomerProducts(false)}>×</button></div>
            <div className="modal-body"><div className="products-list"><h3>List of Products</h3>
              {categories.map(cat => (<div key={cat.id} className="category-group"><h4>{cat.name}</h4>{cat.products.map(prod => (<label key={prod} className="product-checkbox"><input type="checkbox" /> {prod}</label>))}</div>))}
              <button className="btn-add" onClick={() => alert("Products added to customer")}><Plus size={16} /> Add Products</button>
            </div></div>
          </div>
        </div>
      )}

      {/* Group Customers Modal */}
      {showGroupCustomers && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowGroupCustomers(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><button className="back-btn" onClick={() => setShowGroupCustomers(false)}><ArrowLeft size={20} /> Back</button><h2>{selectedGroup.name}</h2><button className="modal-close" onClick={() => setShowGroupCustomers(false)}>×</button></div>
            <div className="modal-body"><div className="members-list">{selectedGroup.members.map(memberId => { const customer = customers.find(c => c.id === memberId); return customer ? (<div key={memberId} className="member-card">{customer.companyName} - {customer.vendorCode}</div>) : null; })}</div>
              <div className="form-actions"><button className="btn-edit" onClick={() => alert("Edit Group")}>Edit Group</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Creditor Component (Supplier List)
  const CreditorContent = () => (
    <div className="content-container">
      <div className="filter-bar"><Search size={18} /><input type="text" placeholder="Search suppliers..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
      <div className="data-table-container"><table className="data-table"><thead><tr><th>Supplier Code</th><th>Company Name</th><th>POC Name</th><th>GST</th><th>Outstanding</th><th>Actions</th></tr></thead>
        <tbody>{filteredData(suppliers, ['companyName', 'pocName', 'vendorCode']).map(sup => (<tr key={sup.id}><td className="highlight">{sup.vendorCode}</td><td>{sup.companyName}</td><td>{sup.pocName}</td><td>{sup.gst}</td><td className="amount">₹0</td><td className="action-buttons"><button className="action-btn view" onClick={() => alert(`Supplier Details:\nCompany: ${sup.companyName}\nPOC: ${sup.pocName}\nAddress: ${sup.address}`)}><Eye size={16} /></button></td></tr>))}</tbody></table></div>
    </div>
  );

  // Create Supplier Component
  const CreateSupplierContent = () => (
    <div className="content-container">
      <form className="customer-form" onSubmit={handleCreateSupplier}>
        <div className="form-grid">
          <div className="form-group"><label>POC Name *</label><input type="text" className="form-input" required placeholder="Enter POC name" /></div>
          <div className="form-group"><label>Company Name *</label><input type="text" className="form-input" required placeholder="Enter company name" /></div>
          <div className="form-group"><label>Username *</label><input type="text" className="form-input" required placeholder="Enter username" /></div>
          <div className="form-group"><label>Password *</label><input type="password" className="form-input" required placeholder="Enter password" /></div>
          <div className="form-group full-width"><label>Address *</label><textarea className="form-textarea" rows="2" placeholder="Enter address"></textarea></div>
          <div className="form-group"><label>PAN Number</label><input type="text" className="form-input" placeholder="Enter PAN" /></div>
          <div className="form-group"><label>Bank Details</label><input type="text" className="form-input" placeholder="Enter bank details" /></div>
          <div className="form-group"><label>GST Number</label><input type="text" className="form-input" placeholder="Enter GST" /></div>
          <div className="form-group"><label>Add Excess Fields</label><input type="text" className="form-input" placeholder="Additional fields" /></div>
          <div className="form-group"><label>Vendor Code</label><input type="text" className="form-input" value="Auto Generated" disabled /></div>
        </div>
        <div className="form-actions"><button type="submit" className="btn-submit"><Check size={16} /> Submit</button></div>
      </form>
    </div>
  );

  // List Supplier Component
  const ListSupplierContent = () => (
    <div className="content-container">
      <div className="filter-bar"><Search size={18} /><input type="text" placeholder="Search suppliers..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
      <div className="customer-list">{filteredData(suppliers, ['companyName', 'vendorCode']).map(sup => (<div key={sup.id} className="customer-card"><div className="customer-header"><h3>{sup.companyName}</h3><span className="vendor-code">{sup.vendorCode}</span></div><div className="customer-details"><p><strong>POC:</strong> {sup.pocName}</p><p><strong>GST:</strong> {sup.gst}</p></div><div className="customer-actions"><button className="action-btn" onClick={() => { setSelectedSupplier(sup); setShowSupplierProducts(true); }}>Select Product</button><button className="action-btn view" onClick={() => alert(`Supplier Details:\nCompany: ${sup.companyName}\nAddress: ${sup.address}\nBank: ${sup.bankDetails}`)}><Eye size={16} /> View</button></div></div>))}</div>
      <div className="group-section"><h3>Group Supplier</h3><div className="group-list">{groups.map(group => (<div key={group.id} className="group-card"><h4>{group.name}</h4><p>{group.members.length} members</p></div>))}</div><button className="btn-add" onClick={() => alert("Create New Group")}><Plus size={16} /> Create New Group</button></div>
      {showSupplierProducts && selectedSupplier && (<div className="modal-overlay" onClick={() => setShowSupplierProducts(false)}><div className="modal-content" onClick={(e) => e.stopPropagation()}><div className="modal-header"><button className="back-btn" onClick={() => setShowSupplierProducts(false)}><ArrowLeft size={20} /> Back</button><h2>Select Products for {selectedSupplier.companyName}</h2><button className="modal-close" onClick={() => setShowSupplierProducts(false)}>×</button></div><div className="modal-body"><div className="products-list"><h3>List of Products</h3>{categories.map(cat => (<div key={cat.id} className="category-group"><h4>{cat.name}</h4>{cat.products.map(prod => (<label key={prod} className="product-checkbox"><input type="checkbox" /> {prod}</label>))}</div>))}<button className="btn-add" onClick={() => alert("Products added to supplier")}><Plus size={16} /> Add Products</button></div></div></div></div>)}
    </div>
  );

  // Product Component
  const ProductContent = () => (
    <div className="content-container">
      <div className="product-options">
        <div className="product-card" onClick={() => alert("Create Category Form")}><div className="product-icon"><FolderTree size={32} color="#4F46E5" /></div><h3>Create Category</h3></div>
        <div className="product-card" onClick={() => alert("List Categories")}><div className="product-icon"><List size={32} color="#10B981" /></div><h3>List Category</h3></div>
        <div className="product-card" onClick={() => alert("Create Product Form")}><div className="product-icon"><Package size={32} color="#F59E0B" /></div><h3>Create Product</h3></div>
        <div className="product-card" onClick={() => alert("List Products")}><div className="product-icon"><List size={32} color="#EF4444" /></div><h3>List Product</h3></div>
        <div className="product-card" onClick={() => alert("Create Group Form")}><div className="product-icon"><Users size={32} color="#8B5CF6" /></div><h3>Create Group</h3></div>
        <div className="product-card" onClick={() => alert("List Groups")}><div className="product-icon"><List size={32} color="#EC4899" /></div><h3>List Group</h3></div>
      </div>
    </div>
  );

  // Create Schedule Component
  const CreateScheduleContent = () => (
    <div className="content-container">
      <form className="schedule-form">
        <div className="form-grid">
          <div className="form-group"><label>Category *</label><select className="form-input"><option value="">Select Category</option>{categories.map(c => <option key={c.id}>{c.name}</option>)}</select></div>
          <div className="form-group"><label>Sub Category *</label><select className="form-input"><option value="">Select Sub Category</option><option>Mobile Phones</option><option>Laptops</option></select></div>
          <div className="form-group"><label>Group</label><select className="form-input"><option value="">Select Group</option><option>Premium Customers</option><option>Regular Customers</option></select></div>
          <div className="form-group"><label>Bought from Customer</label><select className="form-input"><option value="">Select Customer</option>{customers.map(c => <option key={c.id}>{c.companyName}</option>)}</select></div>
          <div className="form-group"><label>Product Name *</label><select className="form-input"><option value="">Select Product</option><option>Product A</option><option>Product B</option></select></div>
          <div className="form-group"><label>No of Quantity *</label><input type="number" className="form-input" placeholder="Enter quantity" /></div>
          <div className="form-group"><label>Weekly</label><input type="checkbox" className="form-checkbox" /></div>
          <div className="form-group"><label>Monthly</label><input type="checkbox" className="form-checkbox" /></div>
          <div className="form-group"><label>Month</label><input type="text" className="form-input" placeholder="Enter month" /></div>
          <div className="form-group"><label>Valid Till Date *</label><input type="date" className="form-input" /></div>
        </div>
        <div className="stock-info"><h3>Stock Details</h3><div className="stock-grid"><div className="stock-card"><h4>Finished Goods Stock</h4><p>Product A: 150 units</p></div><div className="stock-card"><h4>Production Pending Stock</h4><p>Product A: 50 units</p></div><div className="stock-card"><h4>Outsource Pending Stock</h4><p>Product A: 20 units</p></div><div className="stock-card"><h4>Raw Material</h4><p>Product A: 500 units</p></div></div></div>
        <div className="form-actions"><button type="submit" className="btn-submit" onClick={(e) => { e.preventDefault(); alert("Schedule created successfully!"); }}><Check size={16} /> Submit</button></div>
      </form>
    </div>
  );

  // List Schedule Component
  const ListScheduleContent = () => (
    <div className="content-container">
      <div className="filter-bar"><Search size={18} /><input type="text" placeholder="Search schedules..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
      <div className="data-table-container"><table className="data-table"><thead><tr><th>Schedule ID</th><th>Category</th><th>Sub Category</th><th>Product</th><th>Quantity</th><th>Date</th><th>Valid Till</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{schedules.map(sch => (<tr key={sch.id}><td className="highlight">{sch.id}</td><td>{sch.category}</td><td>{sch.subCategory}</td><td>{sch.productName}</td><td>{sch.quantity}</td><td>{sch.date}</td><td>{sch.validTill}</td><td><span className="status approved">Active</span></td><td className="action-buttons"><button className="action-btn view" onClick={() => alert(`Schedule Details:\nProduct: ${sch.productName}\nQuantity: ${sch.quantity}\nValid Till: ${sch.validTill}`)}><Eye size={16} /></button></td></tr>))}</tbody></table></div>
    </div>
  );

  // List Sale Order Component
  const ListSaleOrderContent = () => (
    <div className="content-container">
      <div className="filter-bar"><Search size={18} /><input type="text" placeholder="Search sale orders..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
      <div className="data-table-container"><table className="data-table"><thead><tr><th>Order #</th><th>Customer</th><th>Product</th><th>Quantity</th><th>Amount</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{filteredData(saleOrders, ['orderNumber', 'customer']).map(order => (<tr key={order.id}><td className="highlight">{order.orderNumber}</td><td>{order.customer}</td><td>{order.product}</td><td>{order.quantity}</td><td className="amount">{order.amount}</td><td>{order.date}</td><td><span className={`status ${order.status === 'Approved' ? 'approved' : 'pending'}`}>{order.status}</span></td><td className="action-buttons"><button className="action-btn view" onClick={() => alert(`Order Details:\nCustomer: ${order.customer}\nProduct: ${order.product}\nQuantity: ${order.quantity}\nAmount: ${order.amount}`)}><Eye size={16} /></button>{order.status === 'Pending' && <button className="action-btn approve" onClick={() => handleApproveSaleOrder(order.id)}><Check size={16} /> Approve</button>}</td></tr>))}</tbody></table></div>
    </div>
  );

  // List Invoice Component
  const ListInvoiceContent = () => (
    <div className="content-container">
      <div className="filter-bar"><Search size={18} /><input type="text" placeholder="Search invoices..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /><button className="filter-btn" onClick={() => setFilterOpen(!filterOpen)}><Filter size={16} /> Filter</button></div>
      {filterOpen && (<div className="filter-panel"><select className="filter-select"><option value="">Select Customer</option>{customers.map(c => <option key={c.id}>{c.companyName}</option>)}</select><select className="filter-select"><option value="">Select Schedule</option><option>SCH-001</option></select><input type="date" className="filter-input" /><select className="filter-select"><option value="">Product Group</option><option>Electronics</option><option>Machinery</option></select><button className="apply-filter">Apply</button></div>)}
      <div className="invoice-list">{invoices.map(inv => (<div key={inv.id} className="invoice-card" onClick={() => { setSelectedInvoice(inv); setShowInvoiceDetails(true); }}><div className="invoice-header"><h3>{inv.invoiceNumber}</h3><span className={`status ${inv.status === 'Paid' ? 'approved' : 'pending'}`}>{inv.status}</span></div><div className="invoice-details"><p><strong>Customer:</strong> {inv.customer}</p><p><strong>Amount:</strong> {inv.amount}</p><p><strong>Date:</strong> {inv.date}</p></div></div>))}</div>
      {showInvoiceDetails && selectedInvoice && (<div className="modal-overlay" onClick={() => setShowInvoiceDetails(false)}><div className="modal-content" onClick={(e) => e.stopPropagation()}><div className="modal-header"><h2>Invoice Details - {selectedInvoice.invoiceNumber}</h2><button className="modal-close" onClick={() => setShowInvoiceDetails(false)}>×</button></div><div className="modal-body"><div className="details-grid"><div className="detail-item"><label>Customer:</label><span>{selectedInvoice.customer}</span></div><div className="detail-item"><label>Invoice Value:</label><span className="amount">{selectedInvoice.amount}</span></div><div className="detail-item"><label>CGST:</label><span>{selectedInvoice.cgst}</span></div><div className="detail-item"><label>SGST:</label><span>{selectedInvoice.sgst}</span></div><div className="detail-item"><label>IGST:</label><span>{selectedInvoice.igst}</span></div><div className="detail-item"><label>Date:</label><span>{selectedInvoice.date}</span></div></div><div className="form-actions"><button className="btn-approve" onClick={() => handleCustomerPayment(selectedInvoice)}><CreditCard size={16} /> Receive Payment</button><button className="btn-cancel" onClick={() => setShowInvoiceDetails(false)}>Close</button></div></div></div></div>)}
    </div>
  );

  // Dispatch Component
  const DispatchContent = () => (
    <div className="dispatch-container">
      <div className="dispatch-section"><h3>Select Customer</h3><select className="form-input"><option value="">Select Customer</option>{customers.map(c => <option key={c.id}>{c.companyName}</option>)}</select></div>
      <div className="dispatch-section"><h3>Select Schedule</h3><select className="form-input"><option value="">Select Schedule</option>{schedules.map(s => <option key={s.id}>{s.id}</option>)}</select></div>
      <div className="dispatch-grid"><div className="dispatch-card"><h4>Scheduled List</h4><table className="dispatch-table"><tr><td>Product A</td><td>→</td><td>50 units</td></tr><tr><td>Product B</td><td>→</td><td>30 units</td></tr></table></div>
        <div className="dispatch-card"><h4>Store Details - Finished Goods Stock</h4><table className="dispatch-table"><tr><td>Product A</td><td>→</td><td>150 units</td></tr><tr><td>Product B</td><td>→</td><td>80 units</td></tr></table></div></div>
      <div className="dispatch-grid"><div className="dispatch-card"><h4>Select FG</h4><table className="dispatch-table"><tr><td>Product A</td><td>→</td><td><input type="number" placeholder="Qty" className="qty-input" /></td></tr><tr><td>Product B</td><td>→</td><td><input type="number" placeholder="Qty" className="qty-input" /></td></tr></table><div className="doc-buttons"><button className="action-btn"><FileText size={16} /> Invoice</button><button className="action-btn"><TruckIcon size={16} /> DC</button><button className="action-btn"><Hash size={16} /> E-way Bill</button></div></div>
        <div className="dispatch-card"><h4>Select Scrap</h4><table className="dispatch-table"><tr><td>Scrap 1</td><td>→</td><td><input type="number" placeholder="Qty" className="qty-input" /></td></tr></table><div className="doc-buttons"><button className="action-btn"><FileText size={16} /> Invoice</button><button className="action-btn"><TruckIcon size={16} /> DC</button><button className="action-btn"><Hash size={16} /> E-way Bill</button></div></div></div>
      <div className="dispatch-grid"><div className="dispatch-card"><h4>Select Return</h4><table className="dispatch-table"><tr><td>Return 1</td><td>→</td><td><input type="number" placeholder="Qty" className="qty-input" /></td></tr></table><div className="doc-buttons"><button className="action-btn"><FileText size={16} /> Invoice</button><button className="action-btn"><TruckIcon size={16} /> DC</button><button className="action-btn"><Hash size={16} /> E-way Bill</button></div></div>
        <div className="dispatch-card"><h4>Select NC</h4><table className="dispatch-table"><tr><td>NC 1</td><td>→</td><td><input type="number" placeholder="Qty" className="qty-input" /></td></tr></table><div className="doc-buttons"><button className="action-btn"><FileText size={16} /> Invoice</button><button className="action-btn"><TruckIcon size={16} /> DC</button><button className="action-btn"><Hash size={16} /> E-way Bill</button></div></div></div>
      <div className="dispatch-actions"><button className="btn-submit" onClick={() => alert("Dispatch completed successfully!")}><Truck size={16} /> Process Dispatch</button></div>
    </div>
  );

  // Customer Payment Component
  const CustomerPaymentContent = () => (
    <div className="content-container">
      <div className="filter-bar"><Search size={18} /><input type="text" placeholder="Search by Invoice Number, Customer..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
      <div className="data-table-container"><table className="data-table"><thead><tr><th>Invoice Number</th><th>Customer</th><th>Invoice Value</th><th>CGST</th><th>SGST</th><th>IGST</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{filteredData(invoices, ['invoiceNumber', 'customer']).map(inv => (<tr key={inv.id}><td className="highlight">{inv.invoiceNumber}</td><td className="highlight">{inv.customer}</td><td className="amount">{inv.amount}</td><td className="amount">{inv.cgst}</td><td className="amount">{inv.sgst}</td><td className="amount">{inv.igst}</td><td><span className={`status ${inv.status === 'Paid' ? 'approved' : 'pending'}`}>{inv.status}</span></td><td className="action-buttons"><button className="action-btn approve" onClick={() => handleCustomerPayment(inv)}><CreditCard size={16} /> Receive Payment</button></td></tr>))}</tbody></table></div>
      {showPaymentModal && selectedInvoice && (<div className="modal-overlay" onClick={() => setShowPaymentModal(false)}><div className="modal-content" onClick={(e) => e.stopPropagation()}><div className="modal-header"><h2>Customer Payment - {selectedInvoice.invoiceNumber}</h2><button className="modal-close" onClick={() => setShowPaymentModal(false)}>×</button></div><div className="modal-body"><div className="details-grid"><div className="detail-item"><label>Customer:</label><span>{selectedInvoice.customer}</span></div><div className="detail-item"><label>Invoice Value:</label><span className="amount">{selectedInvoice.amount}</span></div></div><div className="form-group"><label>Payment Mode:</label><select className="form-input" value={paymentDetails.paymentMode} onChange={(e) => setPaymentDetails({...paymentDetails, paymentMode: e.target.value})}><option value="">Select</option><option value="Bank Transfer">Bank Transfer</option><option value="Cheque">Cheque</option><option value="Cash">Cash</option><option value="UPI">UPI</option></select></div><div className="form-group"><label>Payment Date:</label><input type="date" className="form-input" value={paymentDetails.paymentDate} onChange={(e) => setPaymentDetails({...paymentDetails, paymentDate: e.target.value})} /></div><div className="form-group"><label>UTR Number:</label><input type="text" className="form-input" placeholder="Enter UTR number" value={paymentDetails.utrNumber} onChange={(e) => setPaymentDetails({...paymentDetails, utrNumber: e.target.value})} /></div><div className="form-group checkbox"><label><input type="checkbox" checked={paymentDetails.verified} onChange={(e) => setPaymentDetails({...paymentDetails, verified: e.target.checked})} /> Verified</label></div></div><div className="modal-footer"><button className="btn-approve" onClick={handleSubmitPayment}><Check size={16} /> Submit Payment</button><button className="btn-cancel" onClick={() => setShowPaymentModal(false)}>Cancel</button></div></div></div>)}
    </div>
  );

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case "create-ledger": return <CreateLedgerContent />;
      case "debtor": return <DebtorContent />;
      case "create-customer": return <CreateCustomerContent />;
      case "list-customer": return <ListCustomerContent />;
      case "creditor": return <CreditorContent />;
      case "create-supplier": return <CreateSupplierContent />;
      case "list-supplier": return <ListSupplierContent />;
      case "product": return <ProductContent />;
      case "create-schedule": return <CreateScheduleContent />;
      case "list-schedule": return <ListScheduleContent />;
      case "list-sale-order": return <ListSaleOrderContent />;
      case "list-invoice": return <ListInvoiceContent />;
      case "dispatch": return <DispatchContent />;
      case "customer-payment": return <CustomerPaymentContent />;
      default: return <div className="content-container"><h2 className="page-title">{getPageTitle()}</h2></div>;
    }
  };

  return (
    <div className="sales-dashboard-container">
      {/* Sidebar */}
      <div className="sales-sidebar">
        <div className="sales-logo-container">
          <div className="sales-logo-icon"><img src="/rahulerp.jpg" alt="Company Logo" className="sales-logo-image" /></div>
          <div><h2 className="sales-company-name">Ragul Auto Tech</h2><p className="sales-company-tagline">AI Powered ERP</p></div>
        </div>
        <nav className="sales-nav-menu">
          {tabItems.map((tab) => (
            <button key={tab.id} className={`sales-nav-item ${activeTab === tab.id ? 'sales-nav-item-active' : ''}`} onClick={() => handleTabClick(tab.id)}>
              <span className="sales-nav-icon">{tab.icon}</span><span>{tab.label}</span>
            </button>
          ))}
        </nav>
        <div className="sales-logout-container"><button onClick={handleLogoutClick} className="sales-logout-button"><LogOut size={18} /> Logout</button></div>
      </div>

      {/* Main Content */}
      <div className="sales-main-content">
        <header className="sales-header">
          <div><h1 className="sales-main-title">Sales Dashboard</h1><div className="sales-subtitle">Welcome, {user?.role || 'Sales Manager'}</div></div>
          <div className="sales-header-actions"><div className="sales-last-updated">Last updated: {new Date().toLocaleString()}</div></div>
        </header>
        <BackButton />
        <div className="sales-content-card">{renderContent()}</div>
        {showLogoutModal && (<div className="logout-modal-overlay"><div className="logout-modal"><div className="logout-modal-header"><div className="logout-modal-icon">👋</div><h3>Confirm Logout</h3></div><div className="logout-modal-body"><p>Are you sure you want to logout from <strong>Sales</strong> dashboard?</p><p className="logout-modal-warning">You will be redirected to the login page.</p></div><div className="logout-modal-footer"><button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button><button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button></div></div></div>)}
      </div>
    </div>
  );
}